import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { JOB } from '@/constants/testIds';
import ProUpsell from '@/components/ProUpsell';
import Seo from '@/components/Seo';
import { toast, Toaster } from 'sonner';
import { Plus, X, TrendingUp, TrendingDown, Briefcase, AlertTriangle, PoundSterling, Folder, FolderPlus, Trash2, BarChart3, Trophy, Download } from 'lucide-react';

const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const STATUS = {
  not_started: { label: 'Not started', cls: 'bg-slate-200 text-slate-900' },
  in_progress: { label: 'In progress', cls: 'bg-blue-200 text-blue-900' },
  complete:    { label: 'Complete',    cls: 'bg-emerald-200 text-emerald-900' },
};

export default function JobsPage() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.role === 'admin';
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [looseReceipts, setLooseReceipts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState('all'); // 'all' | 'none' | folderId
  const [showCreate, setShowCreate] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadFolders = () => api.get('/jobs/folders').then(r => setFolders(r.data)).catch(() => {});
  const load = async () => {
    try {
      const [j, s] = await Promise.all([api.get('/jobs'), api.get('/jobs/summary')]);
      setJobs(j.data); setSummary(s.data);
      api.get('/jobs/analytics').then(r => setAnalytics(r.data)).catch(() => {});
      api.get('/receipts').then(r => setLooseReceipts((r.data || []).filter(x => !x.attached))).catch(() => {});
    } catch (e) {
      if (e.response?.status !== 402) toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isPro) { load(); loadFolders(); api.get('/projects').then(r => setQuotes(r.data)).catch(() => {}); } else { setLoading(false); } }, [isPro]);

  if (!isPro) return <ProUpsell feature="The Job Tracker" testIdSuffix="jobs" tier="pro" />;

  const visibleJobs = jobs.filter((j) => activeFolder === 'all' ? true : activeFolder === 'none' ? !j.folder_id : j.folder_id === activeFolder);

  const createFolder = async () => {
    const name = window.prompt('New folder name');
    if (!name?.trim()) return;
    try { await api.post('/jobs/folders', { name: name.trim() }); loadFolders(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteFolder = async (id) => {
    if (!window.confirm('Delete this folder? Jobs inside will be moved to "No folder".')) return;
    try { await api.delete(`/jobs/folders/${id}`); if (activeFolder === id) setActiveFolder('all'); loadFolders(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const moveJob = async (jobId, folderId) => {
    const fid = folderId || null;
    setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, folder_id: fid } : j)));  // optimistic
    try { await api.patch(`/jobs/${jobId}/folder`, { folder_id: fid }); loadFolders(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  const exportReport = ({ from, to, label }) => {
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const jobDate = (j) => String(j.start_date || j.created_at || '').slice(0, 10);
    const inRange = (d) => (!from || d >= from) && (!to || d <= to) && d !== '';
    const scoped = jobs.filter((j) => inRange(jobDate(j)));

    // Monthly buckets from scoped jobs
    const byMonth = {};
    scoped.forEach((j) => {
      const key = jobDate(j).slice(0, 7);
      if (!key) return;
      if (!byMonth[key]) byMonth[key] = { profit: 0, jobs: 0 };
      byMonth[key].profit += Number(j.profit || 0);
      byMonth[key].jobs += 1;
    });
    const monthKeys = Object.keys(byMonth).sort();
    const monthLabel = (k) => {
      const [y, m] = k.split('-').map(Number);
      return new Date(y, m - 1, 1).toLocaleString('en-GB', { month: 'short', year: '2-digit' });
    };

    const totalProfit = scoped.reduce((s, j) => s + Number(j.profit || 0), 0);
    const totalQuoted = scoped.reduce((s, j) => s + Number(j.quoted_price || 0), 0);
    const totalNet = scoped.reduce((s, j) => s + Number(j.net_price || 0), 0);
    const totalVat = scoped.reduce((s, j) => s + Number(j.vat_amount || 0), 0);
    const totalInputVat = scoped.reduce((s, j) => s + Number(j.input_vat || 0), 0);
    const recDate = (r) => String(r.date || r.created_at || '').slice(0, 10);
    const looseInRange = looseReceipts.filter((r) => inRange(recDate(r)));
    const looseInputVat = looseInRange.reduce((s, r) => s + Number(r.vat_amount || 0), 0);
    const netVatOwed = totalVat - totalInputVat - looseInputVat;
    const totalCosts = scoped.reduce((s, j) => s + Number(j.total_cost || 0), 0);
    const priced = scoped.filter((j) => Number(j.quoted_price || 0) > 0);
    const avgMargin = priced.length ? (priced.reduce((s, j) => s + Number(j.margin || 0), 0) / priced.length) : 0;

    const rows = [];
    const today = new Date().toISOString().slice(0, 10);
    rows.push(['TradesMate UK — Profit Report']);
    rows.push(['Period', label]);
    if (from || to) rows.push(['Date range', `${from || 'start'} to ${to || 'today'}`]);
    rows.push(['Generated', today]);
    rows.push([]);
    rows.push(['MONTHLY PROFIT (real profit after VAT & costs)']);
    rows.push(['Month', 'Jobs', 'Real profit (£)']);
    if (monthKeys.length === 0) rows.push(['No jobs in this period', '', '']);
    monthKeys.forEach((k) => rows.push([monthLabel(k), byMonth[k].jobs, byMonth[k].profit.toFixed(2)]));
    rows.push([]);
    rows.push(['VAT SUMMARY (for your VAT return)']);
    rows.push(['Total sales incl VAT (£)', totalQuoted.toFixed(2)]);
    rows.push(['Total net turnover ex VAT (£)', totalNet.toFixed(2)]);
    rows.push(['VAT on sales / output VAT (£)', totalVat.toFixed(2)]);
    rows.push(['VAT on job costs / input VAT (£)', totalInputVat.toFixed(2)]);
    rows.push(['VAT on unattached receipts / input VAT (£)', looseInputVat.toFixed(2)]);
    rows.push(['NET VAT OWED TO HMRC (£)', netVatOwed.toFixed(2)]);
    rows.push([]);
    rows.push(['PROFIT SUMMARY']);
    rows.push(['Total net turnover ex VAT (£)', totalNet.toFixed(2)]);
    rows.push(['Total costs (£)', totalCosts.toFixed(2)]);
    rows.push(['Total real profit (£)', totalProfit.toFixed(2)]);
    rows.push(['Average margin (%)', avgMargin.toFixed(1)]);
    rows.push(['Total jobs', scoped.length]);
    rows.push([]);
    rows.push(['ALL JOBS IN PERIOD']);
    rows.push(['Job', 'Customer', 'Status', 'Start date', 'Quoted incl VAT (£)', 'Net ex VAT (£)', 'Output VAT (£)', 'Input VAT (£)', 'Total costs (£)', 'Real profit (£)', 'Margin (%)', 'Expected profit (£)', 'Vs expected (£)']);
    scoped.forEach((j) => rows.push([
      j.name, j.customer_name || '', (j.status || '').replace('_', ' '), j.start_date || '',
      Number(j.quoted_price || 0).toFixed(2), Number(j.net_price || 0).toFixed(2), Number(j.vat_amount || 0).toFixed(2), Number(j.input_vat || 0).toFixed(2),
      Number(j.total_cost || 0).toFixed(2), Number(j.profit || 0).toFixed(2), j.margin,
      Number(j.expected_profit || 0).toFixed(2), Number(j.profit_vs_expected || 0).toFixed(2),
    ]));

    const csv = rows.map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    a.download = `tradesmate-profit-${slug}-${today}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    setShowExport(false);
    toast.success(`Report downloaded — ${scoped.length} job${scoped.length === 1 ? '' : 's'}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Job Tracker" noindex />
      <Toaster position="top-center" richColors />
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-brand font-semibold">Profit on every job</p>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">Job Tracker</h1>
          <p className="text-slate-600 mt-2">Track running costs against your quoted price and see profit live.</p>
        </div>
        <div className="flex items-center gap-2">
          {jobs.length > 0 && (
            <button data-testid="jobs-export-report" onClick={() => setShowExport(true)} className="tm-btn tm-btn-ghost">
              <Download size={16}/> Export report
            </button>
          )}
          <button data-testid={JOB.newBtn} onClick={() => setShowCreate(true)} className="tm-btn">
            <Plus size={16}/> New Job
          </button>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="jobs-summary">
          <SummaryCard icon={<Briefcase size={18}/>} label="In progress" value={summary.in_progress} sub={`${summary.total_jobs} total`} />
          <SummaryCard icon={summary.total_profit >= 0 ? <TrendingUp size={18}/> : <TrendingDown size={18}/>} label="Total profit" value={gbp(summary.total_profit)} sub="after VAT & costs" accent={summary.total_profit >= 0 ? 'green' : 'red'} />
          <SummaryCard icon={<AlertTriangle size={18}/>} label="Losing money" value={summary.losing_money} accent={summary.losing_money > 0 ? 'red' : undefined} />
          <SummaryCard icon={<PoundSterling size={18}/>} label="Outstanding" value={gbp(summary.total_outstanding)} sub="owed by customers" />
        </div>
      )}

      {/* Monthly profit trend */}
      {analytics && analytics.job_count > 0 && <ProfitTrend a={analytics} />}

      {/* Folder filing */}
      <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="jobs-folder-bar">
        <FolderChip label="All jobs" count={jobs.length} active={activeFolder === 'all'} onClick={() => setActiveFolder('all')} />
        <FolderChip label="No folder" count={jobs.filter(j => !j.folder_id).length} active={activeFolder === 'none'} onClick={() => setActiveFolder('none')} />
        {folders.map((f) => (
          <FolderChip key={f.id} label={f.name} count={f.count} active={activeFolder === f.id}
            onClick={() => setActiveFolder(f.id)} onDelete={() => deleteFolder(f.id)} />
        ))}
        <button data-testid="jobs-new-folder" onClick={createFolder} className="rounded-full inline-flex items-center gap-1 px-3 py-1.5 border border-dashed border-slate-300 hover:border-slate-200 text-xs uppercase tracking-widest text-slate-600 font-semibold">
          <FolderPlus size={14}/> New folder
        </button>
      </div>

      {loading ? (
        <p className="font-mono text-slate-500">Loading…</p>
      ) : visibleJobs.length === 0 ? (
        <div className="tm-card p-10 text-center">
          <h3 className="font-display font-bold text-xl uppercase">{jobs.length === 0 ? 'No jobs yet' : 'No jobs in this folder'}</h3>
          <p className="text-slate-600 mt-2">{jobs.length === 0 ? 'Start tracking a job to see your live profit margin.' : 'Move a job here or pick another folder.'}</p>
          {jobs.length === 0 && <button onClick={() => setShowCreate(true)} className="tm-btn mt-4"><Plus size={16}/> New Job</button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
          {visibleJobs.map((j) => <JobCard key={j.id} job={j} folders={folders} onMove={moveJob} />)}
        </div>
      )}

      {showCreate && (
        <CreateJobModal quotes={quotes} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}
      {showExport && (
        <ExportModal onClose={() => setShowExport(false)} onExport={exportReport} />
      )}
    </div>
  );
}

function ProfitTrend({ a }) {
  const months = a.monthly || [];
  const maxAbs = Math.max(1, ...months.map((m) => Math.abs(m.profit)));
  const bestJob = a.best_job;
  const worstJob = a.worst_job;
  const showWorst = worstJob && bestJob && worstJob.id !== bestJob.id;
  return (
    <div className="tm-card p-5 mb-10" data-testid="jobs-profit-trend">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-brand"><BarChart3 size={18} /></span>
        <h2 className="font-display font-semibold text-lg uppercase tracking-tight">Profit trend</h2>
        <span className="text-[11px] font-mono text-slate-400 ml-auto">Last 12 months · real profit (after VAT &amp; costs)</span>
      </div>

      {/* Bar chart */}
      <div className="flex items-end gap-1.5 sm:gap-2 h-40" data-testid="profit-trend-chart">
        {months.map((m) => {
          const pos = m.profit >= 0;
          const h = Math.round((Math.abs(m.profit) / maxAbs) * 100);
          return (
            <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full group relative">
              {m.profit !== 0 && (
                <span className={`text-[9px] font-mono mb-1 ${pos ? 'text-emerald-600' : 'text-rose-600'} opacity-0 group-hover:opacity-100 transition-opacity absolute -top-1 whitespace-nowrap`}>
                  {gbp(m.profit)}
                </span>
              )}
              <div
                className={`w-full rounded-t transition-[height] duration-500 ${pos ? 'bg-brand' : 'bg-rose-400'}`}
                style={{ height: `${Math.max(m.profit !== 0 ? 4 : 0, h)}%` }}
                title={`${m.label}: ${gbp(m.profit)} (${m.jobs} job${m.jobs === 1 ? '' : 's'})`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-1.5 sm:gap-2 mt-1">
        {months.map((m) => (
          <span key={m.month} className="flex-1 text-center text-[9px] font-mono text-slate-400 truncate">{m.label.split(' ')[0]}</span>
        ))}
      </div>

      {/* Best / worst + totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5 pt-5 border-t border-slate-200">
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
          <p className="text-[11px] uppercase tracking-widest text-emerald-700 flex items-center gap-1 font-semibold"><Trophy size={13} /> Best job</p>
          {bestJob ? (
            <>
              <p className="font-display font-bold text-sm mt-1 truncate">{bestJob.name}</p>
              <p className="font-display font-semibold text-xl text-emerald-600">{gbp(bestJob.profit)} <span className="text-xs font-mono text-emerald-500">({bestJob.margin}%)</span></p>
            </>
          ) : <p className="text-sm text-slate-400 mt-1">No priced jobs yet</p>}
        </div>
        <div className={`${showWorst ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'} border rounded p-3`}>
          <p className={`font-mono text-[11px] uppercase tracking-widest flex items-center gap-1 ${showWorst ? 'text-rose-700' : 'text-slate-500'}`}><TrendingDown size={13} /> Lowest job</p>
          {showWorst ? (
            <>
              <p className="font-display font-bold text-sm mt-1 truncate">{worstJob.name}</p>
              <p className={`font-display font-semibold text-xl ${worstJob.profit >= 0 ? 'text-slate-900' : 'text-rose-600'}`}>{gbp(worstJob.profit)} <span className="text-xs font-mono text-slate-400">({worstJob.margin}%)</span></p>
            </>
          ) : <p className="text-sm text-slate-400 mt-1">Add more jobs to compare</p>}
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded p-3">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Avg margin</p>
          <p className="font-display font-semibold text-xl mt-1 text-slate-900">{a.avg_margin}%</p>
          <p className="text-[11px] text-slate-400">across {a.job_count} job{a.job_count === 1 ? '' : 's'}</p>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value, sub, accent }) {
  const color = accent === 'green' ? 'text-emerald-600' : accent === 'red' ? 'text-rose-600' : 'text-slate-900';
  return (
    <div className="tm-card p-4">
      <div className="flex items-center gap-2 text-slate-500"><span className="text-brand">{icon}</span><span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span></div>
      <p className={`font-display font-semibold text-2xl sm:text-3xl mt-1 ${color}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function FolderChip({ label, count, active, onClick, onDelete }) {
  return (
    <span className={`rounded-full inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border cursor-pointer ${active ? 'bg-slate-900 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-800'}`}>
      <button onClick={onClick} className="inline-flex items-center gap-1.5"><Folder size={13}/> {label} <span className={active ? 'text-slate-300' : 'text-slate-400'}>{count}</span></button>
      {onDelete && <button onClick={onDelete} className={`${active ? 'text-slate-400 hover:text-white' : 'text-slate-300 hover:text-rose-600'}`} title="Delete folder"><Trash2 size={12}/></button>}
    </span>
  );
}

function JobCard({ job, folders = [], onMove }) {
  const s = STATUS[job.status] || STATUS.not_started;
  const profitPos = job.profit >= 0;
  return (
    <div className="tm-card p-5 block hover:shadow-lg transition-shadow">
      <Link to={`/jobs/${job.id}`} data-testid={JOB.open(job.id)} className="block">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-display font-bold text-lg uppercase leading-tight">{job.name}</h3>
          {job.customer_name && <p className="text-sm text-slate-500">{job.customer_name}</p>}
        </div>
        <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${s.cls}`}>{s.label}</span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div><p className="text-slate-500 text-xs uppercase tracking-wide">Quoted</p><p className="font-mono font-bold">{gbp(job.quoted_price)}</p></div>
        <div><p className="text-slate-500 text-xs uppercase tracking-wide">Spent</p><p className="font-mono font-bold">{gbp(job.total_cost)}</p></div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 text-sm" data-testid={`job-card-money-${job.id}`}>
        <div><p className="text-slate-500 text-xs uppercase tracking-wide">Received</p><p className="font-mono font-bold text-emerald-600">{gbp(job.received || 0)}</p></div>
        <div>
          <p className="text-slate-500 text-xs uppercase tracking-wide">Outstanding</p>
          <p className={`font-mono font-bold ${(job.owed ?? job.outstanding ?? 0) > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{gbp(job.owed ?? job.outstanding ?? 0)}</p>
          {job.credit > 0 && <p className="text-[10px] font-mono text-amber-600">£{job.credit.toFixed(2)} overpaid</p>}
        </div>
      </div>

      <div className="mt-3 border-t border-slate-200 pt-3 flex items-end justify-between">
        <div>
          <p className="text-slate-500 text-xs uppercase tracking-wide">Real profit</p>
          <p className={`font-display font-semibold text-2xl ${profitPos ? 'text-emerald-600' : 'text-rose-600'}`}>{gbp(job.profit)}</p>
          <p className="text-[11px] text-slate-400 leading-snug">After VAT &amp; costs</p>
          {job.expected_profit > 0 && (
            <p className={`text-[11px] font-mono font-bold mt-0.5 ${job.profit_vs_expected >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {job.profit_vs_expected >= 0 ? '+' : ''}{gbp(job.profit_vs_expected)} vs expected
            </p>
          )}
        </div>
        <span className={`font-mono text-sm font-bold ${profitPos ? 'text-emerald-600' : 'text-rose-600'}`}>{job.margin}%</span>
      </div>

      {/* % complete bar */}
      <div className="mt-3">
        <div className="flex justify-between text-[11px] font-mono text-slate-500 mb-1"><span>Progress</span><span>{job.percent_complete || 0}%</span></div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div className="h-full bg-brand" style={{ width: `${Math.min(100, job.percent_complete || 0)}%` }} />
        </div>
      </div>
      </Link>

      {/* Folder move */}
      <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-2">
        <Folder size={13} className="text-slate-400 shrink-0" />
        <select
          data-testid={`job-folder-select-${job.id}`}
          value={job.folder_id || ''}
          onChange={(e) => onMove && onMove(job.id, e.target.value)}
          className="text-xs font-mono bg-white border border-slate-300 px-2 py-1 flex-1 min-w-0"
        >
          <option value="">No folder</option>
          {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </div>
    </div>
  );
}

function ExportModal({ onClose, onExport }) {
  const now = new Date();
  const y = now.getFullYear();
  // UK tax year runs 6 Apr – 5 Apr. Current tax year starts this year if on/after 6 Apr.
  const afterApr6 = (now.getMonth() > 3) || (now.getMonth() === 3 && now.getDate() >= 6);
  const tyStartYear = afterApr6 ? y : y - 1;
  const ty = (startYear) => ({
    from: `${startYear}-04-06`,
    to: `${startYear + 1}-04-05`,
    label: `Tax year ${startYear}/${String(startYear + 1).slice(2)}`,
  });
  const presets = [
    { key: 'ty-now', ...ty(tyStartYear) },
    { key: 'ty-prev', ...ty(tyStartYear - 1) },
    { key: 'cal', from: `${y}-01-01`, to: `${y}-12-31`, label: `Calendar ${y}` },
    { key: 'all', from: '', to: '', label: 'All time' },
  ];
  const [sel, setSel] = useState('ty-now');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const run = () => {
    if (sel === 'custom') {
      onExport({ from: from || '', to: to || '', label: 'Custom period' });
    } else {
      const p = presets.find((x) => x.key === sel);
      onExport({ from: p.from, to: p.to, label: p.label });
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="tm-card bg-white p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="job-export-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display font-bold text-xl uppercase">Export profit report</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={20}/></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">Choose the period. UK tax year runs 6 April – 5 April.</p>

        <div className="space-y-2">
          {presets.map((p) => (
            <label key={p.key} className={`flex items-center gap-3 p-3 border cursor-pointer ${sel === p.key ? 'border-brand bg-orange-50' : 'border-slate-200 hover:border-slate-400'}`}>
              <input type="radio" name="export-range" checked={sel === p.key} onChange={() => setSel(p.key)} data-testid={`export-preset-${p.key}`} />
              <span className="text-sm uppercase tracking-wide font-semibold">{p.label}
                {p.from && <span className="text-slate-400 normal-case tracking-normal ml-1">({p.from} → {p.to})</span>}
              </span>
            </label>
          ))}
          <label className={`flex items-center gap-3 p-3 border cursor-pointer ${sel === 'custom' ? 'border-brand bg-orange-50' : 'border-slate-200 hover:border-slate-400'}`}>
            <input type="radio" name="export-range" checked={sel === 'custom'} onChange={() => setSel('custom')} data-testid="export-preset-custom" />
            <span className="text-sm uppercase tracking-wide font-semibold">Custom range</span>
          </label>
          {sel === 'custom' && (
            <div className="grid grid-cols-2 gap-3 pl-3">
              <label className="block"><span className="tm-label">From</span>
                <input type="date" className="tm-input" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="export-custom-from" /></label>
              <label className="block"><span className="tm-label">To</span>
                <input type="date" className="tm-input" value={to} onChange={(e) => setTo(e.target.value)} data-testid="export-custom-to" /></label>
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={run} className="tm-btn flex-1" data-testid="export-download-btn"><Download size={16}/> Download CSV</button>
          <button onClick={onClose} className="tm-btn tm-btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function CreateJobModal({ quotes, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', quote_id: '', customer_name: '', quoted_price: '', est_materials: '', est_labour: '', est_overheads: '', est_depreciation: '', est_fuel: '', expected_profit: '', vat_rate: '', status: 'in_progress', percent_complete: 0, start_date: '', target_end_date: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSelectQuote = (id) => {
    const q = quotes.find((x) => x.id === id);
    if (q) {
      const labour = (q.labour_hours || 0) * (q.labour_rate || 0) + (q.labour_days || 0) * (q.labour_day_rate || 0);
      const overheads = (q.labour_days || 0) * (q.overhead_per_day || 0);
      setForm((f) => ({
        ...f, quote_id: id,
        name: f.name || q.name || `Quote ${q.quote_number || ''}`.trim(),
        customer_name: f.customer_name || q.customer_name || '',
        quoted_price: q.total ?? f.quoted_price,
        est_materials: q.materials_cost ?? f.est_materials,
        est_labour: labour || f.est_labour,
        est_overheads: overheads || f.est_overheads,
        expected_profit: q.margin_amount ?? f.expected_profit,
        vat_rate: q.vat_rate ?? f.vat_rate,
      }));
    } else { set('quote_id', ''); }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Give the job a name'); return; }
    setSaving(true);
    try {
      await api.post('/jobs', {
        ...form,
        quote_id: form.quote_id || null,
        quoted_price: parseFloat(form.quoted_price) || 0,
        est_materials: parseFloat(form.est_materials) || 0,
        est_labour: parseFloat(form.est_labour) || 0,
        est_overheads: parseFloat(form.est_overheads) || 0,
        est_depreciation: parseFloat(form.est_depreciation) || 0,
        est_fuel: parseFloat(form.est_fuel) || 0,
        expected_profit: parseFloat(form.expected_profit) || 0,
        vat_rate: parseFloat(form.vat_rate) || 0,
        percent_complete: parseInt(form.percent_complete) || 0,
      });
      toast.success('Job created');
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="tm-card bg-white p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="job-create-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-xl uppercase">New Job</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={20}/></button>
        </div>

        <label className="block mb-3">
          <span className="tm-label">Start from a saved quote (optional)</span>
          <select data-testid="job-create-quote" value={form.quote_id} onChange={(e) => onSelectQuote(e.target.value)} className="tm-input bg-white">
            <option value="">— None / standalone —</option>
            {quotes.map((q) => <option key={q.id} value={q.id}>{q.quote_number ? `${q.quote_number} · ` : ''}{q.name || 'Untitled'} — £{Number(q.total || 0).toLocaleString('en-GB')}</option>)}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-2"><span className="tm-label">Job name</span>
            <input data-testid="job-create-name" className="tm-input" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Smith driveway" /></label>
          <label className="block col-span-2"><span className="tm-label">Customer</span>
            <input className="tm-input" value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)} placeholder="Customer name" /></label>
          <label className="block"><span className="tm-label">Quoted price (£)</span>
            <input data-testid="job-create-quoted" type="number" className="tm-input" value={form.quoted_price} onChange={(e) => set('quoted_price', e.target.value)} placeholder="0" /></label>
          <label className="block"><span className="tm-label">Status</span>
            <select className="tm-input bg-white" value={form.status} onChange={(e) => set('status', e.target.value)}>
              <option value="not_started">Not started</option><option value="in_progress">In progress</option><option value="complete">Complete</option>
            </select></label>
          <label className="block"><span className="tm-label">Est. materials (£)</span>
            <input type="number" className="tm-input" value={form.est_materials} onChange={(e) => set('est_materials', e.target.value)} placeholder="0" /></label>
          <label className="block"><span className="tm-label">Est. labour (£)</span>
            <input type="number" className="tm-input" value={form.est_labour} onChange={(e) => set('est_labour', e.target.value)} placeholder="0" /></label>
          <label className="block"><span className="tm-label">Overhead cost (£)</span>
            <input type="number" className="tm-input" value={form.est_overheads} onChange={(e) => set('est_overheads', e.target.value)} placeholder="0" /></label>
          <label className="block"><span className="tm-label">Depreciation & repair (£)</span>
            <input type="number" className="tm-input" value={form.est_depreciation} onChange={(e) => set('est_depreciation', e.target.value)} placeholder="0" /></label>
          <label className="block"><span className="tm-label">Fuel cost (£)</span>
            <input type="number" className="tm-input" value={form.est_fuel} onChange={(e) => set('est_fuel', e.target.value)} placeholder="0" /></label>
          <label className="block"><span className="tm-label">Expected profit (£)</span>
            <input type="number" className="tm-input" value={form.expected_profit} onChange={(e) => set('expected_profit', e.target.value)} placeholder="0" /></label>
          <label className="block"><span className="tm-label">VAT rate (%)</span>
            <input type="number" className="tm-input" value={form.vat_rate} onChange={(e) => set('vat_rate', e.target.value)} placeholder="0" /></label>
          <label className="block"><span className="tm-label">Start date</span>
            <input type="date" className="tm-input" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} /></label>
          <label className="block"><span className="tm-label">Target end</span>
            <input type="date" className="tm-input" value={form.target_end_date} onChange={(e) => set('target_end_date', e.target.value)} /></label>
        </div>

        <button data-testid={JOB.createSubmit} onClick={submit} disabled={saving} className="tm-btn w-full mt-5">{saving ? 'Creating…' : 'Create job'}</button>
      </div>
    </div>
  );
}
